import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import _ from 'lodash';
import moment from 'moment';
import './NotificationsRegistryEntry.scss';

import {
  getNotificationRegistryEntry,
  updateNotificationRegistryEntry,
  deleteNotificationRegistryEntry,
  retireNotificationRegistryEntry,
  restoreNotificationRegistryEntry,
  saveNotificationCopyDraft,
  draftForNotification,
  approveNotificationCopy,
  setNotificationCopyLiveInFlow,
  withdrawNotificationCopy,
  revokeNotificationCopyApproval,
  acknowledgeNotificationPlanDifference,
  updateNotificationRollout,
  updateNotificationLaunchPlan,
  searchNotificationRecipients,
  getNotificationTemplateStarter,
  getNotificationRegistryOptions,
  getNotificationRowGroupCandidates,
  createNotificationRowGroup,
  updateNotificationRowGroup,
  leaveNotificationRowGroup,
  withdrawNotificationCopyDraft,
  renderEmailTemplate
} from '../../api/notifications';
import { downloadTextFile } from '../Catalogs/catalogArtifactFiles';
import UserSearchPicker from '../General/UserSearchPicker';
import InfoTip from './elements/InfoTip';
import NotificationMoveButton from './elements/NotificationMoveButton';
import LaunchPlanField from './elements/LaunchPlanField';
import SentFromSelect from './elements/SentFromSelect';
import DraftButton from './elements/DraftButton';
import SwitchBuilder, { emptySwitchDefinition, switchProposalGaps } from './elements/SwitchBuilder';
import SharedRowCard from './elements/SharedRowCard';
import ButtonVerbField from './elements/ButtonVerbField';
import { confirmAlert } from 'react-confirm-alert';
import { confirmRetire } from './elements/confirmRetire';
import PushPreview from './elements/PushPreview';
import EmailPreview from './elements/EmailPreview';
import {
  AUDIENCE_LABELS,
  CENTER_BEHAVIOR_LABELS,
  CENTER_CTA_VERBS,
  CENTER_IMAGE_LABELS,
  CHANNEL_LABELS,
  COPY_STATUS_LABELS,
  SURFACE_LABELS,
  SURFACE_NOTES,
  OPTIONAL_SURFACES,
  DRAFTABLE_SURFACES,
  canDraftFromPlan,
  SENT_FROM_CHOICES,
  SENT_FROM_UNDECIDED,
  sentFromChoiceOf,
  sentFromFieldsFor,
  DELIVERED_BY_KLAVIYO_DELIVERY,
  DELIVERED_BY_KLAVIYO_EVENT_HINT,
  DELIVERED_BY_KLAVIYO_PLANNED_EVENT_HINT,
  DELIVERED_BY_KLAVIYO_COPY_NOTE,
  DELIVERED_BY_KLAVIYO_EMAIL_BODY,
  KLAVIYO_FLOW_URL_INVALID,
  TIPS,
  SHADOW_PERIOD_NONE,
  HOLDOUT_NONE,
  AUTO_LIVE_NOTE,
  AUTO_LIVE_REQUIRES_NO,
  approveWarningFor,
  withdrawWarningFor,
  revokeWarningFor,
  modeChangeConfirm,
  modeChangeToast,
  retireConfirmFor,
  retireToast,
  restoreToast,
  holdoutChangeConfirm,
  holdoutChangeToast,
  launchMovesFor,
  UNSET_MODE_NOTE,
  launchStageFor,
  humanizeStage,
  humanizeSentFrom,
  humanizeBudgetCategory,
  interpolateWithExamples,
  fieldGuideFor,
  copyFieldLabel,
  hasCopyWords,
  sendsFromBackend,
  klaviyoCopyNote,
  isKlaviyoOwnedCopySurface,
  backendDeliveredByKlaviyo,
  usesKlaviyoFlow,
  isKlaviyoDeliveredSurface,
  isKlaviyoFlowUrl,
  parseInputsText,
  formatInputsText,
  copyVariablePills,
  shouldRefuseUnknownCopyInputs,
  literalExampleVariable,
  validateCopyContent,
  settingsGroupsFor,
  CASE_OPERATORS,
  CASE_OPERATOR_LABELS,
  CASE_FIELDS,
  MAX_CASES,
  supportsCases,
  casesOf,
  validateCaseSet,
  conflictingCasePairs,
  caseFieldLabel,
  parseStatuses,
  statusLabelOf,
  lockedCases,
  THREAD_CASE_FIELD,
  isEntityThread,
  formatCaseEnum,
  parseCaseEnumText,
  declaredSwitchOutsideGroups,
  rowPathFor,
  caseStepKey,
  FIRST_STEP
} from '../../helpers/notification_registry_helpers';
import { matchScrollHeight } from '../../helpers/helpers';
import Tooltip from '../General/Tooltip';

const searchAllowlistUsers = query => searchNotificationRecipients(query).then(data => data.users || []);

const NEW_SWITCH = '__new__';

// Draft with AI writes from Purpose and Trigger, so both have to be there.

// One notification: the launch checklist, what code enforces, what the team decided, the words, a
// preview, and history. Plan rows and copy fields edit in place and save when you leave them.
const NotificationsRegistryEntry = ({ type, onBack, canChangeRollout = false, onOpenTemplate = null, onOpenEntry = null }) => {
  const [page, setPage] = useState(null);
  const [options, setOptions] = useState(null);
  const [isFetching, setIsFetching] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [drafting, setDrafting] = useState(null);
  const [allowlistDraft, setAllowlistDraft] = useState(null);

  useEffect(() => {
    getNotificationRegistryOptions()
      .then(setOptions)
      .catch(() => setOptions({}));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setIsFetching(true);
    setLoadError(null);
    getNotificationRegistryEntry(type)
      .then(resp => !cancelled && setPage(resp))
      .catch(err => !cancelled && setLoadError(err?.error || 'Could not load it.'))
      .finally(() => !cancelled && setIsFetching(false));
    return () => (cancelled = true);
  }, [type]);

  const apply = (request, failure) =>
    request
      .then(resp => {
        setPage(resp);
        return resp;
      })
      .catch(err => {
        window.ALERT?.error?.(err?.error || err?.message || failure);
        return null;
      });

  const applyWithLiveConfirmation = (request, retry, failure) =>
    request.then(
      resp => {
        setPage(resp);
        return resp;
      },
      err => {
        if (err?.extra?.code === 'CONFIRMATION_REQUIRED') {
          const dialog = modeChangeConfirm({
            name: page.entry.name,
            mode: 'live',
            fromMode: page.rulebook.mode,
            status: page.entry.status,
            holdoutPercent: page.launchPlan?.holdoutPercent,
            allowlistCount: (page.allowlistUsers || []).length
          });
          confirmAlert({
            title: dialog.title,
            message: err.error || dialog.message,
            buttons: [
              { label: 'Cancel', className: 'cancel' },
              { label: dialog.confirm, onClick: () => apply(retry(), failure) }
            ]
          });
          return null;
        }
        window.ALERT?.error?.(err?.error || err?.message || failure);
        return null;
      }
    );

  const savePlanFields = fields => apply(updateNotificationRegistryEntry(type, fields), 'Could not save that change.');
  const savePlan = (field, value) => savePlanFields({ [field]: value });
  // The behavior decides which plan fields, surfaces and cases apply, so changing it clears what depended on the old one.
  const changeCenterBehavior = behavior => {
    if (behavior === (page?.entry?.declaredCenterBehavior || 'none')) return;
    confirmAlert({
      title: 'Change the Notification Center behavior?',
      message: 'Fields and copy that only fit the current behavior are cleared or stop applying. Everything that fits the new behavior stays.',
      // Cancelling re-renders so the select snaps back to the saved behavior.
      afterClose: () => setPage(current => ({ ...current })),
      buttons: [
        { label: 'Cancel', className: 'cancel' },
        {
          label: 'Change behavior',
          onClick: () =>
            apply(
              updateNotificationRegistryEntry(type, {
                declaredCenterBehavior: behavior,
                declaredCenterWindow: behavior === 'aggregated' ? 'day' : null,
                declaredThreadEntity: null,
                declaredThreadStatuses: null,
                ...(behavior === 'own_row' || behavior === 'aggregated' ? {} : { declaredCaseEnum: null }),
                ...(behavior === 'none' ? { declaredCenterImageOf: null } : {})
              }),
              'Could not change the behavior.'
            )
        }
      ]
    });
  };
  const changeCenterWindow = centerWindow => {
    if (centerWindow === (page?.entry?.declaredCenterWindow || 'day')) return;
    confirmAlert({
      title: 'Change the aggregation window?',
      message: 'Thread fields and copy that do not fit the new window stop applying. They return if you switch back.',
      afterClose: () => setPage(current => ({ ...current })),
      buttons: [
        { label: 'Cancel', className: 'cancel' },
        {
          label: 'Change window',
          onClick: () =>
            apply(
              updateNotificationRegistryEntry(type, {
                declaredCenterWindow: centerWindow,
                ...(centerWindow === 'entity' ? { declaredCaseEnum: null } : {})
              }),
              'Could not change the window.'
            )
        }
      ]
    });
  };
  const saveDraft = (surface, content) =>
    applyWithLiveConfirmation(
      saveNotificationCopyDraft(type, surface, content),
      () => saveNotificationCopyDraft(type, surface, content, { confirmLive: true }),
      'Could not save the draft.'
    );
  const withdrawDraft = surface =>
    applyWithLiveConfirmation(
      withdrawNotificationCopyDraft(type, surface),
      () => withdrawNotificationCopyDraft(type, surface, { confirmLive: true }),
      'Could not withdraw the draft.'
    );
  const approve = surface => {
    const askToGoLive = serverMessage => {
      const dialog = modeChangeConfirm({
        name: page.entry.name,
        mode: 'live',
        fromMode: page.rulebook.mode,
        status: page.entry.status,
        holdoutPercent: page.launchPlan?.holdoutPercent,
        allowlistCount: (page.allowlistUsers || []).length
      });
      confirmAlert({
        title: dialog.title,
        message: serverMessage || dialog.message,
        buttons: [
          { label: 'Cancel', className: 'cancel' },
          {
            label: dialog.confirm,
            onClick: () => apply(approveNotificationCopy(type, surface, { confirmLive: true }), 'Could not approve the copy.')
          }
        ]
      });
    };
    const mayGoLive =
      !!page?.entry?.autoLive &&
      !!page?.rulebook?.templateName &&
      !(page?.launchPlan?.shadowPeriodDays > 0) &&
      !(page?.launchPlan?.holdoutPercent > 0);
    confirmAlert({
      title: 'Are you sure you want to continue?',
      message: mayGoLive
        ? 'If approving completes the gate, this notification moves to Live and starts sending. That asks for a second confirmation before anything is sent.'
        : approveWarningFor({
            surface,
            sentFrom: page?.entry?.sentFrom,
            hasTemplate: !!page?.rulebook?.templateName,
            pushDelivery: page?.rulebook?.pushDelivery,
            emailDelivery: page?.rulebook?.emailDelivery
          }),
      buttons: [
        { label: 'Cancel', className: 'cancel' },
        {
          label: 'Confirm',
          onClick: () =>
            approveNotificationCopy(type, surface).then(
              resp => {
                setPage(resp);
                return resp;
              },
              err => {
                if (err?.extra?.code === 'CONFIRMATION_REQUIRED') {
                  askToGoLive(err.error);
                  return null;
                }
                window.ALERT?.error?.(err?.error || err?.message || 'Could not approve the copy.');
                return null;
              }
            )
        }
      ]
    });
  };
  const setLiveInFlow = (surface, liveInFlow) =>
    confirmAlert({
      title: liveInFlow ? 'Mark this copy Live in flow?' : 'Move this copy back to Approved?',
      message: liveInFlow
        ? 'Only continue if this exact approved copy is already in the live Klaviyo flow. This records that fact in the Registry; it does not update Klaviyo.'
        : 'This moves the Registry status back to Approved. It does not remove or change copy in Klaviyo; update the flow separately if these words are no longer live.',
      buttons: [
        { label: 'Cancel', className: 'cancel' },
        {
          label: liveInFlow ? 'Mark live' : 'Move back',
          onClick: () =>
            applyWithLiveConfirmation(
              setNotificationCopyLiveInFlow(type, surface, liveInFlow),
              () => setNotificationCopyLiveInFlow(type, surface, liveInFlow, { confirmLive: true }),
              'Could not record that.'
            )
        }
      ]
    });
  const changeMode = mode => {
    const state = {
      name: page.entry.name,
      mode,
      fromMode: page.rulebook.mode,
      status: page.entry.status,
      holdoutPercent: page.launchPlan?.holdoutPercent,
      allowlistCount: (page.allowlistUsers || []).length
    };
    const dialog = modeChangeConfirm(state);
    confirmAlert({
      title: dialog.title,
      message: dialog.message,
      buttons: [
        { label: 'Cancel', className: 'cancel' },
        {
          label: dialog.confirm,
          onClick: () =>
            apply(updateNotificationRollout(type, { mode }), 'Could not change the mode.').then(
              resp => resp && window.ALERT?.success?.(modeChangeToast(state))
            )
        }
      ]
    });
  };
  const saveLaunchPlan = fields =>
    applyWithLiveConfirmation(
      updateNotificationLaunchPlan(type, fields),
      () => updateNotificationLaunchPlan(type, { ...fields, confirm: true }),
      'Could not change the launch plan.'
    ).then(resp => {
      if (resp && 'holdoutPercent' in fields)
        window.ALERT?.success?.(
          holdoutChangeToast({ name: page.entry.name, live: page.rulebook.mode === 'live', percent: resp.launchPlan?.holdoutPercent })
        );
      return resp;
    });
  const changeLaunchPlan = fields => {
    const dialog =
      'holdoutPercent' in fields &&
      holdoutChangeConfirm({
        name: page.entry.name,
        live: page.rulebook.mode === 'live',
        from: page.launchPlan?.holdoutPercent || 0,
        to: fields.holdoutPercent
      });
    if (!dialog) return saveLaunchPlan(fields);
    confirmAlert({
      title: dialog.title,
      message: dialog.message,
      buttons: [
        { label: 'Cancel', className: 'cancel' },
        { label: dialog.confirm, onClick: () => saveLaunchPlan(fields) }
      ]
    });
  };
  const savedAllowlistUsers = page?.rulebook?.mode === 'allowlist' ? page.allowlistUsers || [] : [];
  const allowlistUsers = allowlistDraft || savedAllowlistUsers;
  useEffect(() => {
    setAllowlistDraft(null);
  }, [page]);
  const changeAllowlist = users => {
    setAllowlistDraft(users);
    return apply(
      updateNotificationRollout(type, { mode: 'allowlist', allowlistUserIds: users.map(user => user.id) }),
      'Could not update the allowlist.'
    ).then(resp => {
      if (!resp) setAllowlistDraft(null);
      return resp;
    });
  };
  const createRowGroup = () =>
    applyWithLiveConfirmation(
      createNotificationRowGroup(type, {}),
      () => createNotificationRowGroup(type, { confirm: true }),
      'Could not open the shared row.'
    );
  const updateRowGroup = body =>
    applyWithLiveConfirmation(
      updateNotificationRowGroup(type, body),
      () => updateNotificationRowGroup(type, { ...body, confirm: true }),
      'Could not change the shared row.'
    );
  // Leaving is what puts a lane back on its own words, so it asks first: nothing is revived for it.
  const leaveRowGroup = () =>
    confirmAlert({
      title: 'Leave the shared row?',
      message: 'This notification stops sharing a Notification Center row. Its own Center copy is whatever is approved on it, which may be nothing.',
      buttons: [
        { label: 'Cancel', className: 'cancel' },
        {
          label: 'Leave',
          onClick: () =>
            applyWithLiveConfirmation(
              leaveNotificationRowGroup(type),
              () => leaveNotificationRowGroup(type, { confirm: true }),
              'Could not leave the shared row.'
            )
        }
      ]
    });
  const remove = () =>
    confirmAlert({
      title: 'Are you sure you want to delete this planned notification?',
      buttons: [
        { label: 'Cancel', className: 'cancel' },
        {
          label: 'Delete',
          onClick: () =>
            deleteNotificationRegistryEntry(type)
              .then(onBack)
              .catch(err => window.ALERT?.error?.(err?.error || err?.message || 'Could not delete it.'))
        }
      ]
    });

  const retire = () => {
    const current = page.entry;
    const hasTemplate = !!page.rulebook.templateName;
    const ask = retireConfirmFor({
      name: current.name,
      stage: launchStageFor({ ...current, hasTemplate, mode: page.rulebook.mode }),
      allowlistCount: (page.allowlistUsers || []).length,
      sentFrom: current.sentFrom,
      hasTemplate,
      holdoutPercent: page.launchPlan?.holdoutPercent || 0,
      ownsSharedWords: page.membership?.wordsLiveOnType === current.type && (page.membership.members || []).some(member => member.type !== current.type)
    });
    confirmRetire({
      ...ask,
      onRetire: reason =>
        apply(retireNotificationRegistryEntry(type, reason), 'Could not retire it.').then(
          resp => resp && window.ALERT?.success?.(retireToast({ name: current.name, sentFrom: current.sentFrom, hasTemplate }))
        )
    });
  };
  const restore = () =>
    confirmAlert({
      title: `Restore ${page.entry.name}?`,
      message: 'It returns to Planned. Nothing sends until someone moves it.',
      buttons: [
        { label: 'Cancel', className: 'cancel' },
        {
          label: 'Restore',
          onClick: () =>
            apply(restoreNotificationRegistryEntry(type), 'Could not restore it.').then(
              resp => resp && window.ALERT?.success?.(restoreToast(page.entry.name))
            )
        }
      ]
    });

  // Withdrawing is the moment an approved variant stops sending, so it asks first.
  const withdraw = surface =>
    confirmAlert({
      title: 'Withdraw this copy?',
      message: withdrawWarningFor({
        surface,
        sentFrom: page?.entry?.sentFrom,
        hasTemplate: !!page?.rulebook?.templateName,
        pushDelivery: page?.rulebook?.pushDelivery,
        emailDelivery: page?.rulebook?.emailDelivery
      }),
      buttons: [
        { label: 'Cancel', className: 'cancel', onClick: () => {} },
        {
          label: 'Withdraw',
          onClick: () =>
            applyWithLiveConfirmation(
              withdrawNotificationCopy(type, surface),
              () => withdrawNotificationCopy(type, surface, { confirmLive: true }),
              'Could not withdraw the copy.'
            )
        }
      ]
    });
  const revokeApproval = surface =>
    confirmAlert({
      title: 'Revoke this approval?',
      message: revokeWarningFor({
        surface,
        sentFrom: page?.entry?.sentFrom,
        hasTemplate: !!page?.rulebook?.templateName,
        pushDelivery: page?.rulebook?.pushDelivery,
        emailDelivery: page?.rulebook?.emailDelivery,
        hasDraft: !!page?.copy?.find(candidate => candidate.surface === surface)?.draft
      }),
      buttons: [
        { label: 'Cancel', className: 'cancel' },
        { label: 'Revoke approval', onClick: () => apply(revokeNotificationCopyApproval(type, surface), 'Could not revoke the approval.') }
      ]
    });
  // Draft with AI writes drafts on the server, credited to it, so the page that comes back already shows them.
  const draft = ({ surfaces = [], want = 'copy', key }) => {
    setDrafting(key);
    return apply(draftForNotification(type, { surfaces, want }), 'Could not draft that.')
      .then(resp => resp?.issues?.length && window.ALERT?.warn?.(resp.issues.join(' ')))
      .finally(() => setDrafting(null));
  };

  // A sweep link can name a short code that has since been renamed or removed; the server's reason is shown as is.
  if (isFetching || !page) {
    return (
      <div className='notifications-registry-entry'>
        <button type='button' className='back-link' onClick={onBack}>
          ← Registry
        </button>
        <div className='loading'>{isFetching ? 'Loading…' : loadError}</div>
      </div>
    );
  }

  const { entry, rulebook, rollout, launchPlan, inputs, copy, emailPreview, gate, history, membership } = page;
  const owner = entry.owner;
  const stage = launchStageFor({ ...entry, hasTemplate: !!rulebook.templateName, mode: rulebook.mode });
  // A page that does not say which action applies still offers Delete on an unclaimed plan.
  const canDelete = page.actions ? page.actions.delete : entry.status === 'planned' && !rulebook.templateName;
  const canRetire = !!page.actions?.retire && canChangeRollout;
  const canRestore = !!page.actions?.restore && canChangeRollout;

  return (
    <div className='notifications-registry-entry'>
      <div className='entry-header'>
        <div>
          <button type='button' className='back-link' onClick={onBack}>
            ← Registry
          </button>
          <h2 className='entry-title'>
            {entry.name}
            <span className={cn('badge', `stage-${stage}`)} title={stage === 'shadow' && !rulebook.onLadder ? UNSET_MODE_NOTE : ''}>
              {humanizeStage(stage)}
            </span>
            {membership && (
              <span className={cn('badge', `shared-${membership.role}`)}>
                {membership.role === 'owner' ? 'Owner' : 'Follower'} · {membership.groupKey}
              </span>
            )}
          </h2>
          <div className='entry-code'>{entry.type}</div>
          <div className='entry-meta'>
            <span>
              <b>Sent from</b> {humanizeSentFrom(entry.sentFrom, { pushDelivery: rulebook.pushDelivery })}
            </span>
            <span>
              <b>Audience</b> {(entry.audiences || []).map(audience => AUDIENCE_LABELS[audience] || audience).join(', ')}
            </span>
            <span>
              <b>Owner</b> {owner ? owner.name || owner.email : '—'}
            </span>
            <span>
              <b>Area</b> {(entry.productAreas || []).join(', ') || '—'}
            </span>
            <span>
              <b>Category</b> {humanizeBudgetCategory(rulebook.budgetCategory)}
            </span>
            {entry.status === 'retired' && (
              <span>
                {sendsFromBackend({ sentFrom: entry.sentFrom, hasTemplate: !!rulebook.templateName })
                  ? 'Nothing sends.'
                  : `${entry.sentFrom === 'onesignal_dashboard' ? 'OneSignal' : 'Klaviyo'} keeps sending until someone stops it there.`}
              </span>
            )}
          </div>
        </div>
        <div className='entry-actions'>
          {canRestore && (
            <button type='button' className='quiet' onClick={restore}>
              Restore to Planned
            </button>
          )}
          {canRetire && (
            <button type='button' className='quiet' onClick={retire}>
              Retire
            </button>
          )}
          {canDelete && (
            <button type='button' className='quiet' onClick={remove}>
              Delete Planned Notification
            </button>
          )}
          {rulebook.templateName && onOpenTemplate && (
            <button type='button' className='quiet' onClick={() => onOpenTemplate(rulebook.templateName)}>
              Open in Templates
            </button>
          )}
        </div>
      </div>

      <GateCard gate={gate} isLive={entry.status === 'live' && rulebook.mode !== 'shadow'} />

      {entry.differsFromPlan?.length > 0 && !entry.differsFromPlanAcknowledgedAt && (
        <div className='differs-notice'>
          <span className='badge gate-blocked'>Differs from plan</span>
          <span>
            The template says {entry.differsFromPlan.map(diff => `${diff.field}: ${diff.inCode} (plan said ${diff.planned})`).join('; ')}. The
            template wins.
          </span>
          <button type='button' className='quiet' onClick={() => apply(acknowledgeNotificationPlanDifference(type), 'Could not record that.')}>
            Acknowledge
          </button>
        </div>
      )}

      <div className='entry-columns'>
        <div className='column'>
          <RulebookCard
            rulebook={rulebook}
            rollout={rollout}
            launchPlan={launchPlan}
            entry={entry}
            stage={stage}
            gate={gate}
            canChangeRollout={canChangeRollout}
            allowlistUsers={allowlistUsers}
            onChangeMode={changeMode}
            onChangeAllowlist={changeAllowlist}
            onChangeLaunchPlan={changeLaunchPlan}
            onDownloadStarter={() =>
              getNotificationTemplateStarter(type)
                .then(text => downloadTextFile(`${entry.type}.js`, text))
                .catch(err => window.ALERT?.error?.(err?.error || 'Could not build the starter.'))
            }
          />
          <PlanCard
            entry={entry}
            inputs={inputs}
            rulebook={rulebook}
            options={options}
            onSave={savePlan}
            onChangeCenterBehavior={changeCenterBehavior}
            onChangeCenterWindow={changeCenterWindow}
            onSaveFields={savePlanFields}
            onDraftInputs={canDraftFromPlan(entry) ? () => draft({ want: 'inputs', key: 'inputs' }) : null}
            isDraftingInputs={drafting === 'inputs'}
            isBusy={!!drafting}
          />
          {(rulebook.centerBehavior === 'aggregated' || membership) && (
            <SharedRowCard
              type={entry.type}
              membership={membership}
              canShare={!!rulebook.centerAnchor}
              isThread={isEntityThread(rulebook)}
              anchor={rulebook.centerAnchor}
              onOpen={onOpenEntry || (() => {})}
              onCreate={createRowGroup}
              onUpdate={updateRowGroup}
              onLeave={leaveRowGroup}
              onLoadCandidates={() => getNotificationRowGroupCandidates(type)}
            />
          )}
        </div>
        <div className='column'>
          <CopyCard
            copy={copy}
            inputs={inputs}
            entry={entry}
            rulebook={rulebook}
            templateName={rulebook.templateName}
            onSaveDraft={saveDraft}
            onWithdrawDraft={withdrawDraft}
            onApprove={approve}
            onLiveInFlow={setLiveInFlow}
            onWithdraw={withdraw}
            onRevokeApproval={revokeApproval}
            onOpenTemplate={onOpenTemplate && rulebook.templateName ? () => onOpenTemplate(rulebook.templateName) : null}
            onOpenEntry={onOpenEntry}
            onDraft={(surfaces, key) => draft({ surfaces, key })}
            drafting={drafting}
          />
          <PreviewCard copy={copy} inputs={inputs} templateName={rulebook.templateName} emailPreview={emailPreview} />
          <HistoryCard history={history} />
        </div>
      </div>
    </div>
  );
};

NotificationsRegistryEntry.propTypes = {
  type: PropTypes.string.isRequired,
  onBack: PropTypes.func.isRequired,
  canChangeRollout: PropTypes.bool,
  onOpenTemplate: PropTypes.func,
  onOpenEntry: PropTypes.func
};

// The launch checklist: one row per check, a mark, what is true now, and who fixes it.
// The checklist leads the page until the notification is live with every check passed; then it folds
// into one line, since a launched notification has nothing left to unblock.
const GateCard = ({ gate, isLive }) => {
  const [isOpen, setIsOpen] = useState(false);
  if (isLive && gate.passed && !isOpen) {
    return (
      <div className='gate-line'>
        <span className='mark ok'>✓</span>
        <span>
          Live. Launch checklist passed, {gate.total} of {gate.total}.
        </span>
        <button type='button' className='link' onClick={() => setIsOpen(true)}>
          Show
        </button>
      </div>
    );
  }
  return (
    <div className={cn('gate-card', gate.passed ? 'ready' : 'blocked')}>
      <div className='gate-head'>
        <b>{isLive ? 'Live.' : gate.passed ? 'Ready to leave shadow.' : 'Not ready to leave shadow.'}</b>
        <span className='gate-count'>
          {gate.passedCount} of {gate.total}
        </span>
        <InfoTip message={TIPS.gate} />
        {isLive && gate.passed && (
          <button type='button' className='link' onClick={() => setIsOpen(false)}>
            Hide
          </button>
        )}
      </div>
      {gate.checks.map(check => (
        <div key={check.key} className='gate-check'>
          <span className={cn('mark', check.passed ? 'ok' : 'no')}>{check.passed ? '✓' : '✕'}</span>
          <span className='check-label'>{check.label}</span>
          <span className='check-detail'>{check.detail}</span>
          <span className='check-owner'>{check.passed ? 'done' : check.owner}</span>
        </div>
      ))}
    </div>
  );
};

GateCard.propTypes = { gate: PropTypes.object.isRequired, isLive: PropTypes.bool.isRequired };

const PROVISIONAL_NOTES = {
  not_in_code_yet: 'not in code yet · chosen on New Planned Notification, until the template lands',
  no_template: 'no template · sent outside our code'
};

const AutoLiveField = ({ on, allowed, canEdit, onChange }) => (
  <div className='mode-control'>
    {canEdit ? (
      <>
        <button
          type='button'
          className={cn('mode-segment', { current: on })}
          disabled={on || !allowed}
          onClick={() => onChange(true)}
        >
          Yes
        </button>
        <button type='button' className={cn('mode-segment', { current: !on })} disabled={!on} onClick={() => onChange(false)}>
          No
        </button>
      </>
    ) : (
      <span>{on ? 'Yes' : 'No'}</span>
    )}
    <span className='mode-extra'>{AUTO_LIVE_NOTE}</span>
    {!allowed && <span className='mode-extra auto-live-warn'>{AUTO_LIVE_REQUIRES_NO}</span>}
  </div>
);

AutoLiveField.propTypes = {
  on: PropTypes.bool.isRequired,
  allowed: PropTypes.bool.isRequired,
  canEdit: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired
};

// What code enforces, read-only. Before a template exists the values are the ones chosen at creation.
const RulebookCard = ({
  rulebook,
  rollout,
  launchPlan,
  entry,
  stage,
  gate,
  canChangeRollout,
  allowlistUsers,
  onChangeMode,
  onChangeAllowlist,
  onChangeLaunchPlan,
  onDownloadStarter
}) => {
  const provisional = rulebook.provisional;
  const rows = [
    {
      label: 'Template',
      value: rulebook.templateName ? (
        <code>{rulebook.templateName}.js</code>
      ) : (
        <span>
          <span className='muted'>none yet</span> ·{' '}
          <button type='button' className='link' onClick={onDownloadStarter}>
            download starter
          </button>
        </span>
      )
    },
    { label: 'Budget category', value: humanizeBudgetCategory(rulebook.budgetCategory) },
    {
      label: 'Mute switch',
      value:
        rulebook.userNotificationSetting === 'none' ? (
          <span>
            No switch <span className='muted'>{entry.noSwitchReason ? `· ${entry.noSwitchReason}` : '· reason missing'}</span>
          </span>
        ) : rulebook.userNotificationSetting ? (
          <code>{rulebook.userNotificationSetting}</code>
        ) : (
          <span className='muted'>not chosen</span>
        )
    },
    {
      label: 'Notification Center',
      value: `${isEntityThread(rulebook) ? 'Live Thread' : CENTER_BEHAVIOR_LABELS[rulebook.centerBehavior] || 'Not decided'}${
        rulebook.centerImageOf ? ` · ${CENTER_IMAGE_LABELS[rulebook.centerImageOf]}` : ''
      }${rulebook.requiresAction ? ' · Needs action' : ''}`
    },
    ...(isEntityThread(rulebook)
      ? [
          {
            label: 'Thread',
            value: `${rulebook.threadEntity || 'nothing chosen'} · ${
              rulebook.threadStatuses?.length ? rulebook.threadStatuses.map(statusLabelOf).join(' → ') : 'no statuses'
            }`
          }
        ]
      : []),
    { label: 'Channels', value: (rulebook.channels || []).map(channel => CHANNEL_LABELS[channel]).join(' · ') || '—' }
  ];
  const isBackend = !entry.sentFrom || entry.sentFrom === 'backend';
  const hasLaunchRow = !!rulebook.onLadder;
  const blocked = (gate.checks || []).find(check => !check.passed);
  const moves = launchMovesFor({ stage, mode: rulebook.mode, gatePassed: gate.passed, lockedReason: blocked?.detail || blocked?.label });
  return (
    <div className='card'>
      <div className='card-head'>
        <span className='card-title'>
          Rulebook
          <InfoTip message={TIPS.rulebook} />
          {provisional && <span className='tag'>{provisional === 'no_template' ? 'no template' : 'not in code yet'}</span>}
        </span>
        <span className='card-note'>{provisional ? PROVISIONAL_NOTES[provisional] : 'from the template, read-only'}</span>
      </div>
      <div className='kv'>
        {rows.map(row => (
          <React.Fragment key={row.label}>
            <div className='k'>{row.label}</div>
            <div className='v'>{row.value}</div>
          </React.Fragment>
        ))}
        {isBackend && (
          <>
            <div className='k'>
              Mode
              <InfoTip message={TIPS.mode} />
            </div>
            <div className='v'>
              {!rulebook.templateName ? (
                <span className='muted'>on the ladder once the template lands</span>
              ) : (
                <div className='mode-control'>
                  <span className={cn('badge', `stage-${stage}`)}>{humanizeStage(stage)}</span>
                  {moves.map(move => (
                    <NotificationMoveButton key={move.mode} move={move} enabled={canChangeRollout} onClick={() => onChangeMode(move.mode)} />
                  ))}
                  {rulebook.mode === 'allowlist' && canChangeRollout ? (
                    <div className='mode-allowlist'>
                      <UserSearchPicker
                        selectedUsers={allowlistUsers}
                        onChange={onChangeAllowlist}
                        searchUsers={searchAllowlistUsers}
                        placeholder='Search by name, username, email, or user id'
                        showUserIds
                      />
                    </div>
                  ) : (
                    rollout && <span className='mode-extra'>{rollout.allowlistUserIds.length} on the list</span>
                  )}
                  {!hasLaunchRow && <span className='mode-extra'>{UNSET_MODE_NOTE}</span>}
                </div>
              )}
            </div>
            <div className='k'>
              Shadow period
              <InfoTip message={TIPS.shadowPeriod} />
            </div>
            <div className='v'>
              <LaunchPlanField
                value={launchPlan.shadowPeriodDays}
                source={launchPlan.shadowPeriodSource}
                unit='days'
                max={60}
                yesValue={7}
                canEdit={canChangeRollout}
                describe={days =>
                  days
                    ? `Lands in Shadow and records what it would send. Allowlist and Live unlock after ${days} days in Shadow with at least one send to read.`
                    : SHADOW_PERIOD_NONE
                }
                onSave={days => onChangeLaunchPlan({ shadowPeriodDays: days })}
              />
            </div>
            <div className='k'>
              Holdout group
              <InfoTip message={TIPS.holdout} />
            </div>
            <div className='v'>
              <LaunchPlanField
                value={launchPlan.holdoutPercent}
                source={launchPlan.holdoutSource}
                unit='%'
                max={50}
                yesValue={10}
                canEdit={canChangeRollout}
                describe={percent =>
                  percent
                    ? `Once Live, ${percent}% of creators never get the push or the email. They still get the Notification Center row.`
                    : HOLDOUT_NONE
                }
                onSave={percent => onChangeLaunchPlan({ holdoutPercent: percent })}
              />
            </div>
            <div className='k'>Auto-Live</div>
            <div className='v'>
              <AutoLiveField
                on={!!entry.autoLive}
                allowed={!launchPlan.shadowPeriodDays && !launchPlan.holdoutPercent}
                canEdit={canChangeRollout}
                onChange={autoLive => onChangeLaunchPlan({ autoLive })}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

RulebookCard.propTypes = {
  rulebook: PropTypes.object.isRequired,
  rollout: PropTypes.object,
  launchPlan: PropTypes.object.isRequired,
  entry: PropTypes.object.isRequired,
  stage: PropTypes.string.isRequired,
  gate: PropTypes.object.isRequired,
  canChangeRollout: PropTypes.bool.isRequired,
  allowlistUsers: PropTypes.array.isRequired,
  onChangeMode: PropTypes.func.isRequired,
  onChangeAllowlist: PropTypes.func.isRequired,
  onChangeLaunchPlan: PropTypes.func.isRequired,
  onDownloadStarter: PropTypes.func.isRequired
};

const PLAN_ROWS = [
  {
    field: 'declaredCenterBehavior',
    label: 'Notification Center behavior',
    tip: TIPS.centerBehavior,
    isCenterBehavior: true,
    untilTemplateBehavior: true
  },
  {
    field: 'declaredRequiresAction',
    label: 'Needs action',
    tip: TIPS.needsAction,
    isNeedsAction: true,
    centerRowOnly: true,
    untilTemplateBehavior: true
  },
  { field: 'declaredCtaStyle', label: 'Button style', tip: TIPS.ctaStyle, isCtaStyle: true, needsActionOnly: true },
  { field: 'declaredShowChevron', label: 'Chevron', tip: TIPS.showChevron, isChevron: true, timelineOnly: true },
  {
    field: 'declaredCenterImageOf',
    label: 'Notification Center image',
    tip: TIPS.centerImage,
    isCenterImage: true,
    centerRowOnly: true,
    untilTemplateBehavior: true
  },
  { field: 'sentFrom', label: 'Sent from', tip: TIPS.sentFrom, isSentFrom: true },
  { field: 'klaviyoDelivery', label: 'Delivery', isKlaviyoDelivery: true, klaviyoDeliveredOnly: true },
  { field: 'klaviyoEventName', label: 'Klaviyo event', isKlaviyoEvent: true, klaviyoDeliveredOnly: true },
  { field: 'klaviyoFlowLink', label: 'Klaviyo flow', tip: TIPS.klaviyoUrl, isKlaviyoFlowLink: true, flowLinkOnly: true },
  { field: 'declaredUserNotificationSetting', label: 'Mute switch (optional)', tip: TIPS.muteSwitch, isSwitch: true, untilTemplateSwitch: true },
  { field: 'noSwitchReason', label: 'Why no mute switch', tip: TIPS.noSwitchReason, multiline: true, noSwitchOnly: true },
  { field: 'purpose', label: 'Purpose (optional)', tip: TIPS.purpose, multiline: true },
  { field: 'triggerDescription', label: 'Trigger (optional)', tip: TIPS.trigger, multiline: true },
  { field: 'inputs', label: 'Inputs (optional)', tip: TIPS.inputs, multiline: true, isInputs: true },
  { field: 'tapDestination', label: 'Tap lands', tip: TIPS.tapLands },
  {
    field: 'declaredCenterWindow',
    label: 'Aggregation window',
    aggregationOnly: true,
    untilTemplateBehavior: true,
    isCenterWindow: true
  },
  { field: 'declaredCenterAnchor', label: 'Row anchor', tip: TIPS.rowAnchor, aggregationOnly: true },
  { field: 'declaredCaseEnum', label: 'Sentence branches on', tip: TIPS.caseEnum, enumOnly: true, isCaseEnum: true },
  { field: 'declaredThreadEntity', label: 'Thread on', tip: TIPS.threadOn, threadOnly: true },
  {
    field: 'declaredThreadStatuses',
    label: 'Statuses the row moves through',
    tip: TIPS.threadStatuses,
    threadOnly: true,
    untilTemplateStatuses: true,
    isStatuses: true
  },
  { field: 'emergencyOff', label: 'Emergency off (optional)', tip: TIPS.emergencyOff, multiline: true },
  { field: 'notificationCenterRationale', label: 'Notification Center rationale (optional)', tip: TIPS.notificationCenterRationale, multiline: true },
  { field: 'notes', label: 'Notes (optional)', tip: TIPS.notes, multiline: true }
];

const canDeclareMuteSwitch = rulebook => !!rulebook.provisional || !rulebook.userNotificationSetting || rulebook.userNotificationSetting === 'none';

// What the team decided. Click a value to edit; it saves when you leave the field. Sent from and the
// mute switch are choices, so they are selects; the switch stays until a template names one.
const PlanCard = ({
  entry,
  inputs,
  rulebook,
  options,
  onSave,
  onSaveFields,
  onChangeCenterBehavior,
  onChangeCenterWindow,
  onDraftInputs = null,
  isDraftingInputs = false,
  isBusy = false
}) => {
  const groups = settingsGroupsFor(options, entry.audiences);
  const isDeliveredByKlaviyo = backendDeliveredByKlaviyo({ sentFrom: entry.sentFrom, pushDelivery: rulebook.pushDelivery });
  const usesFlow = usesKlaviyoFlow({ sentFrom: entry.sentFrom, pushDelivery: rulebook.pushDelivery });
  // A template sends from our backend; until the row says so it stays editable, since the Gate reads the stored value.
  const isSentFromLocked = rulebook.source === 'template' && entry.sentFrom === 'backend';
  const sentFromChoice = sentFromChoiceOf({ sentFrom: entry.sentFrom, pushDelivery: rulebook.pushDelivery });
  const storedSwitchOutsideGroups = declaredSwitchOutsideGroups(options, entry, groups);
  const canEditMuteSwitch = canDeclareMuteSwitch(rulebook);
  // A proposal is held here until it is saved, so typing a label does not write a History line per keystroke.
  const [proposal, setProposal] = useState(entry.newSwitchDefinition || null);
  const [revising, setRevising] = useState(false);
  const chooseSwitch = value => {
    if (value === NEW_SWITCH) {
      setProposal(entry.newSwitchDefinition || emptySwitchDefinition(groups));
      setRevising(!entry.newSwitchDefinition);
      return;
    }
    setProposal(null);
    setRevising(false);
    onSaveFields({ declaredUserNotificationSetting: value || null, ...(entry.newSwitchDefinition ? { newSwitchDefinition: null } : {}) });
  };
  const storedProposal = entry.newSwitchDefinition || null;
  const proposalMatchesStored = !!storedProposal && _.isEqual(_.omit(proposal, 'settingEdited'), storedProposal);
  const editingProposal = !!proposal && (!proposalMatchesStored || revising);
  const saveProposal = () =>
    onSaveFields({ declaredUserNotificationSetting: proposal.setting, newSwitchDefinition: _.omit(proposal, 'settingEdited') }).then(saved => {
      if (saved) setRevising(false);
    });
  return (
    <div className='card'>
      <div className='card-head'>
        <span className='card-title'>
          Plan
          <InfoTip message={TIPS.plan} />
        </span>
        <span className='card-note'>editable</span>
      </div>
      <div className='kv'>
        {PLAN_ROWS.filter(
          row =>
            (!row.flowLinkOnly || usesFlow) &&
            (!row.klaviyoDeliveredOnly || isDeliveredByKlaviyo) &&
            (!row.untilTemplateSwitch || canEditMuteSwitch) &&
            (!row.aggregationOnly || rulebook.centerBehavior === 'aggregated') &&
            (!row.threadOnly || isEntityThread(rulebook)) &&
            (!row.enumOnly ||
              ((rulebook.centerBehavior === 'own_row' || rulebook.centerBehavior === 'aggregated') &&
                !isEntityThread(rulebook) &&
                rulebook.source !== 'template')) &&
            // once a template lands, its code owns the behavior and the statuses
            (!row.untilTemplateBehavior || rulebook.source !== 'template') &&
            (!row.untilTemplateStatuses || rulebook.source !== 'template') &&
            (!row.centerRowOnly || (rulebook.centerBehavior && rulebook.centerBehavior !== 'none')) &&
            (!row.needsActionOnly || rulebook.requiresAction) &&
            (!row.timelineOnly || (rulebook.centerBehavior && rulebook.centerBehavior !== 'none' && !rulebook.requiresAction)) &&
            // the Gate wants the reason whether the none came from the plan or from the template
            (!row.noSwitchOnly || rulebook.userNotificationSetting === 'none')
        ).map(row => (
          <React.Fragment key={row.field}>
            <div className='k'>
              {row.label}
              {row.tip && <InfoTip message={row.tip} />}
            </div>
            <div className='v'>
              {row.isNeedsAction ? (
                <select
                  className='inline-select'
                  value={entry.declaredRequiresAction ? 'yes' : 'no'}
                  onChange={e => {
                    const yes = e.target.value === 'yes';
                    onSaveFields({
                      declaredRequiresAction: yes,
                      ...(yes ? { declaredShowChevron: null } : { declaredCtaStyle: null })
                    });
                  }}
                >
                  <option value='no'>No</option>
                  <option value='yes'>Yes</option>
                </select>
              ) : row.isCtaStyle ? (
                <select
                  className='inline-select'
                  value={entry.declaredCtaStyle || 'primary'}
                  onChange={e => onSave('declaredCtaStyle', e.target.value)}
                >
                  <option value='primary'>Primary</option>
                  <option value='secondary'>Secondary</option>
                </select>
              ) : row.isChevron ? (
                <select
                  className='inline-select'
                  value={entry.declaredShowChevron ? 'yes' : 'no'}
                  onChange={e => onSave('declaredShowChevron', e.target.value === 'yes')}
                >
                  <option value='no'>No</option>
                  <option value='yes'>Yes</option>
                </select>
              ) : row.isCenterImage ? (
                <select
                  className='inline-select'
                  data-testid='entry-center-image'
                  value={entry.declaredCenterImageOf || ''}
                  onChange={e => e.target.value && onSave('declaredCenterImageOf', e.target.value)}
                >
                  {!entry.declaredCenterImageOf && <option value=''>Not chosen</option>}
                  {(options?.centerImageKinds || ['link', 'brand', 'general']).map(value => (
                    <option key={value} value={value}>
                      {CENTER_IMAGE_LABELS[value] || value}
                    </option>
                  ))}
                </select>
              ) : row.isCenterBehavior ? (
                <select
                  className='inline-select'
                  data-testid='entry-center-behavior'
                  value={entry.declaredCenterBehavior || 'none'}
                  onChange={e => onChangeCenterBehavior(e.target.value)}
                >
                  {(options?.centerBehaviors || []).map(value => (
                    <option key={value} value={value}>
                      {CENTER_BEHAVIOR_LABELS[value] || value}
                    </option>
                  ))}
                </select>
              ) : row.isCenterWindow ? (
                <select className='inline-select' value={entry.declaredCenterWindow || 'day'} onChange={e => onChangeCenterWindow(e.target.value)}>
                  {(options?.centerWindows || []).map(value => (
                    <option key={value} value={value}>
                      Per {value}
                    </option>
                  ))}
                </select>
              ) : row.isSentFrom && isSentFromLocked ? (
                <SentFromSelect compact locked value={sentFromChoice} />
              ) : row.isKlaviyoDelivery ? (
                DELIVERED_BY_KLAVIYO_DELIVERY
              ) : row.isKlaviyoEvent && rulebook.source === 'template' ? (
                <>
                  <code>{rulebook.klaviyoEventName || '—'}</code>
                  <div className='hint'>{DELIVERED_BY_KLAVIYO_EVENT_HINT}</div>
                </>
              ) : row.isKlaviyoEvent ? (
                <>
                  <InlineText
                    key={entry.declaredKlaviyoEventName || ''}
                    value={entry.declaredKlaviyoEventName || ''}
                    emptyLabel='Click to add'
                    onSave={value => onSave('declaredKlaviyoEventName', value.trim() || null)}
                  />
                  <div className='hint'>{DELIVERED_BY_KLAVIYO_PLANNED_EVENT_HINT}</div>
                </>
              ) : row.isKlaviyoFlowLink ? (
                <KlaviyoFlowLink key={entry.klaviyoFlowUrl || ''} url={entry.klaviyoFlowUrl || ''} onSave={url => onSave('klaviyoFlowUrl', url)} />
              ) : row.isSentFrom ? (
                <>
                  <SentFromSelect
                    compact
                    value={sentFromChoice}
                    onChange={choice =>
                      onSaveFields(rulebook.source === 'template' ? _.pick(sentFromFieldsFor(choice), 'sentFrom') : sentFromFieldsFor(choice))
                    }
                  />
                  <div className='hint'>{(_.find(SENT_FROM_CHOICES, { value: sentFromChoice }) || SENT_FROM_UNDECIDED).description}</div>
                </>
              ) : row.isSwitch ? (
                <>
                  <select
                    className='inline-select'
                    value={proposal ? NEW_SWITCH : entry.declaredUserNotificationSetting || ''}
                    onChange={e => chooseSwitch(e.target.value)}
                  >
                    <option value=''>Not chosen</option>
                    <option value='none'>No switch · cannot be muted</option>
                    {groups.map(group => (
                      <optgroup key={group.id || group.title} label={group.title}>
                        {group.options.map(option => (
                          <option key={option.setting} value={option.setting}>
                            {option.label} · {option.setting}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                    {storedSwitchOutsideGroups && (
                      <option value={storedSwitchOutsideGroups.setting}>
                        {storedSwitchOutsideGroups.label} · {storedSwitchOutsideGroups.setting}
                      </option>
                    )}
                    <option value={NEW_SWITCH}>+ Propose a new switch…</option>
                  </select>
                  {proposal && <div className='hint'>See next section below</div>}
                </>
              ) : row.isStatuses ? (
                <InlineText
                  key={(entry.declaredThreadStatuses || []).join(',')}
                  value={(entry.declaredThreadStatuses || []).join(', ')}
                  emptyLabel='Click to add'
                  onSave={value => onSave('declaredThreadStatuses', parseStatuses(value))}
                />
              ) : row.isCaseEnum ? (
                <InlineText
                  key={formatCaseEnum(entry.declaredCaseEnum)}
                  value={formatCaseEnum(entry.declaredCaseEnum)}
                  emptyLabel='Click to add'
                  onSave={value => {
                    const parsed = parseCaseEnumText(value);
                    if (String(value || '').trim() && !parsed) return;
                    onSave('declaredCaseEnum', parsed);
                  }}
                />
              ) : row.isInputs ? (
                <InputsEditor
                  key={formatInputsText(entry.inputs || [])}
                  inputs={inputs}
                  planInputs={entry.inputs || []}
                  fromTemplate={inputs.some(input => input.source === 'template')}
                  onSave={value => onSave('inputs', value)}
                  onDraft={onDraftInputs}
                  isDrafting={isDraftingInputs}
                  isBusy={isBusy}
                />
              ) : (
                <InlineText
                  key={entry[row.field] || ''}
                  value={entry[row.field] || ''}
                  multiline={row.multiline}
                  emptyLabel='Click to add'
                  onSave={value => onSave(row.field, value)}
                />
              )}
            </div>
          </React.Fragment>
        ))}
      </div>
      {proposal && canEditMuteSwitch && (
        <>
          <SwitchBuilder
            groups={groups}
            definition={proposal}
            onChange={setProposal}
            settled={!editingProposal}
            onEdit={editingProposal ? null : () => setRevising(true)}
          />
          {editingProposal && (
            <div className='proposal-actions'>
              <button type='button' className='approve' onClick={saveProposal} disabled={!!switchProposalGaps(proposal).length}>
                Save proposal
              </button>
              <button
                type='button'
                className='quiet'
                onClick={() => {
                  setProposal(storedProposal);
                  setRevising(false);
                }}
              >
                Cancel
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

PlanCard.propTypes = {
  entry: PropTypes.object.isRequired,
  inputs: PropTypes.array.isRequired,
  rulebook: PropTypes.object.isRequired,
  options: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  onChangeCenterBehavior: PropTypes.func.isRequired,
  onChangeCenterWindow: PropTypes.func.isRequired,
  onSaveFields: PropTypes.func.isRequired,
  onDraftInputs: PropTypes.func,
  isDraftingInputs: PropTypes.bool,
  isBusy: PropTypes.bool
};

// A plan value that turns into a field on click and saves on blur.
const InlineText = ({ value, multiline = false, emptyLabel, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const finish = () => {
    setEditing(false);
    if (draft !== value) onSave(draft);
  };
  if (!editing) {
    return (
      <div className={cn('inline-text', { empty: !value })} onClick={() => setEditing(true)}>
        {value || emptyLabel}
        <span className='pencil'>✎</span>
      </div>
    );
  }
  if (multiline) {
    return (
      <textarea
        autoFocus
        ref={field => matchScrollHeight(field)}
        rows={1}
        className='inline-field'
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={finish}
      />
    );
  }
  return (
    <input
      autoFocus
      className='inline-field'
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={finish}
      onKeyDown={e => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        finish();
      }}
    />
  );
};

InlineText.propTypes = {
  value: PropTypes.string.isRequired,
  multiline: PropTypes.bool,
  emptyLabel: PropTypes.string,
  onSave: PropTypes.func.isRequired
};

// The flow link a plan value opens to edit; Save waits for a link the Gate accepts as a Klaviyo flow.
const KlaviyoFlowLink = ({ url, onSave }) => {
  const [draft, setDraft] = useState(null);
  if (draft == null) {
    const edit = (
      <button type='button' className='pencil' aria-label='Edit the Klaviyo flow link' onClick={() => setDraft(url)}>
        ✎
      </button>
    );
    if (!url)
      return (
        <div className='flow-link'>
          <span className='flow-link-missing'>No flow link yet</span>
          <button type='button' className='quiet' onClick={() => setDraft('')}>
            Add link
          </button>
        </div>
      );
    if (!isKlaviyoFlowUrl(url))
      return (
        <>
          <div className='flow-link'>
            <span className='flow-link-missing'>{url}</span>
            {edit}
          </div>
          <div className='hint invalid'>{KLAVIYO_FLOW_URL_INVALID}</div>
        </>
      );
    return (
      <div className='flow-link'>
        <a href={url} target='_blank' rel='noopener noreferrer'>
          Open in Klaviyo ↗
        </a>
        {edit}
      </div>
    );
  }
  const typed = draft.trim();
  const isValid = isKlaviyoFlowUrl(typed);
  const save = () => isValid && onSave(typed).then(saved => saved && setDraft(null));
  return (
    <div className='flow-link-edit'>
      <input
        autoFocus
        className={cn('inline-field', { invalid: !!typed && !isValid })}
        value={draft}
        placeholder='https://www.klaviyo.com/flow/…/edit'
        onChange={e => setDraft(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') save();
          if (e.key === 'Escape') setDraft(null);
        }}
      />
      {!!typed && !isValid && <div className='hint invalid'>{KLAVIYO_FLOW_URL_INVALID}</div>}
      <div className='flow-link-actions'>
        <button type='button' className='save' disabled={!isValid} onClick={save}>
          Save
        </button>
        <button type='button' className='quiet' onClick={() => setDraft(null)}>
          Cancel
        </button>
      </div>
    </div>
  );
};

KlaviyoFlowLink.propTypes = {
  url: PropTypes.string.isRequired,
  onSave: PropTypes.func.isRequired
};

// Inputs as chips with their examples; editable as "name: example" lines until the template declares them.
const InputsEditor = ({ inputs, planInputs, fromTemplate, onSave, onDraft = null, isDrafting = false, isBusy = false }) => {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(formatInputsText(planInputs));
  if (editing) {
    return (
      <textarea
        autoFocus
        ref={field => matchScrollHeight(field)}
        className='inline-field mono'
        rows={1}
        value={text}
        onChange={e => setText(e.target.value)}
        onBlur={() => {
          setEditing(false);
          // Compared as text: a stored input may carry a status or no example, which the lines do not show.
          if (text.trim() !== formatInputsText(planInputs)) onSave(parseInputsText(text));
        }}
      />
    );
  }
  return (
    <div className={cn('inputs-value', { empty: !inputs.length, locked: fromTemplate })} onClick={() => !fromTemplate && setEditing(true)}>
      {inputs.length
        ? inputs.map(input => (
            <span key={input.name} className='input-chip'>
              <code>{input.name}</code>
              {input.example != null && input.example !== '' && <span className='example'>{input.example}</span>}
              {input.isObject && <span className='muted small'>bare name not insertable</span>}
            </span>
          ))
        : 'Click to add, one per line or split by / , or ; as name: example'}
      {!fromTemplate && !inputs.length && (
        <DraftButton
          onClick={onDraft}
          isDrafting={isDrafting}
          isBusy={isBusy}
          title='Proposes the inputs from Purpose and Trigger; engineering confirms them.'
        />
      )}
      {fromTemplate ? <span className='muted small'>send contract from template</span> : <span className='pencil'>✎</span>}
    </div>
  );
};

InputsEditor.propTypes = {
  inputs: PropTypes.array.isRequired,
  planInputs: PropTypes.array.isRequired,
  fromTemplate: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  onDraft: PropTypes.func,
  isDrafting: PropTypes.bool,
  isBusy: PropTypes.bool
};

// The words. One sentence says which surfaces change here and which live in code; each surface is
// the field itself, holding what a creator gets today. Push, Notification Center and the email
// subject and preview are editable; the email body is read-only, since it lives in the template.
const CopyCard = ({
  copy,
  inputs,
  entry,
  rulebook,
  templateName,
  onSaveDraft,
  onWithdrawDraft,
  onApprove,
  onLiveInFlow,
  onWithdraw,
  onRevokeApproval,
  onOpenTemplate,
  onOpenEntry,
  onDraft,
  drafting
}) => {
  const canDraft = canDraftFromPlan(entry);
  const isDeliveredByKlaviyo = backendDeliveredByKlaviyo({ sentFrom: entry.sentFrom, pushDelivery: rulebook.pushDelivery });
  const pushBApproved = !!copy.find(state => state.surface === 'push_b')?.latestApproved;
  // The card's button writes every required surface that is still empty; optional ones wait for their own button.
  const emptyRequired = copy
    .filter(state => state.status === 'needs_copy' && DRAFTABLE_SURFACES.includes(state.surface) && !OPTIONAL_SURFACES.includes(state.surface))
    .filter(state => !ownsKlaviyoCopy(state.surface, { sentFrom: entry.sentFrom, templateName, rulebook }))
    .map(state => state.surface);
  const path = rowPathFor(rulebook);
  const [step, setStep] = useState(FIRST_STEP);
  const activeStep = path?.steps.some(candidate => candidate.key === step) ? step : FIRST_STEP;
  return (
    <div className='card copy-card'>
      <div className='card-head stacked'>
        <div className='card-heading'>
          <h3>
            Copy
            <InfoTip message={TIPS.copy} />
          </h3>
          {emptyRequired.length > 0 && (
            <DraftButton
              onClick={canDraft ? () => onDraft(emptyRequired, 'all') : null}
              isDrafting={drafting === 'all'}
              isBusy={!!drafting}
              title={TIPS.draftWithAi}
            />
          )}
        </div>
        <p className={cn('card-intro', { 'flow-note': isDeliveredByKlaviyo })}>
          {isDeliveredByKlaviyo ? DELIVERED_BY_KLAVIYO_COPY_NOTE : copyIntro(copy, { sentFrom: entry.sentFrom, templateName, rulebook })}
        </p>
      </div>
      {copy.map(state => (
        <React.Fragment key={`${state.surface}:${JSON.stringify(state.draft?.content || state.latestApproved?.content || {})}`}>
          {path && state.surface === 'center_row' && <RowPath path={path} step={activeStep} onStep={setStep} />}
          <SurfaceBlock
            state={state}
            inputs={inputs}
            entry={entry}
            templateName={templateName}
            emailDelivery={rulebook.emailDelivery}
            pushDelivery={rulebook.pushDelivery}
            path={path}
            step={activeStep}
            onSaveDraft={onSaveDraft}
            onWithdrawDraft={onWithdrawDraft}
            onApprove={onApprove}
            onLiveInFlow={onLiveInFlow}
            onWithdraw={onWithdraw}
            onRevokeApproval={onRevokeApproval}
            pushBApproved={pushBApproved}
            onOpenTemplate={onOpenTemplate}
            onOpenEntry={onOpenEntry}
            onDraft={canDraft ? () => onDraft([state.surface], state.surface) : null}
            isDrafting={drafting === state.surface || drafting === 'all'}
            isBusy={!!drafting}
          />
        </React.Fragment>
      ))}
      {!copy.length && <div className='empty-surfaces'>No channels chosen yet, so nothing to write.</div>}
    </div>
  );
};

CopyCard.propTypes = {
  copy: PropTypes.array.isRequired,
  inputs: PropTypes.array.isRequired,
  entry: PropTypes.object.isRequired,
  rulebook: PropTypes.object.isRequired,
  templateName: PropTypes.string,
  onSaveDraft: PropTypes.func.isRequired,
  onWithdrawDraft: PropTypes.func.isRequired,
  onApprove: PropTypes.func.isRequired,
  onLiveInFlow: PropTypes.func.isRequired,
  onWithdraw: PropTypes.func.isRequired,
  onRevokeApproval: PropTypes.func.isRequired,
  onOpenTemplate: PropTypes.func,
  onOpenEntry: PropTypes.func,
  onDraft: PropTypes.func.isRequired,
  drafting: PropTypes.string
};

// The same aggregated row as the day grows, one step per sentence it can show. Choosing a step lights
// the sentence that step uses, so the first box and the several cases read as one path, not a list.
const RowPath = ({ path, step, onStep }) => {
  const current = path.steps.find(candidate => candidate.key === step) || path.steps[0];
  return (
    <div className='row-path'>
      <p className='row-path-label'>How this row changes through the day</p>
      <div className='row-path-steps'>
        {path.steps.map((candidate, index) => (
          <React.Fragment key={candidate.key}>
            {index > 0 && <span className='row-path-arrow'>→</span>}
            <button
              type='button'
              className={cn('row-path-step', { on: candidate.key === current.key })}
              aria-pressed={candidate.key === current.key}
              onClick={() => onStep(candidate.key)}
            >
              <span className='when'>{candidate.when}</span>
              <span className='uses'>{candidate.uses}</span>
            </button>
          </React.Fragment>
        ))}
      </div>
      <p className='row-path-line'>{current.note}</p>
    </div>
  );
};

RowPath.propTypes = {
  path: PropTypes.object.isRequired,
  step: PropTypes.string.isRequired,
  onStep: PropTypes.func.isRequired
};

// Surfaces the model writes; the button is chosen from a list and the email body lives in the template.

const SEND_TIME_GROUPS = [
  { label: 'Push', surfaces: ['push_a', 'push_b'] },
  { label: 'the email subject', surfaces: ['email_subject'] }
];
const SIGN_OFF_GROUPS = [
  { label: 'Notification Center', surfaces: ['center_row', 'center_row_several', 'center_cta'] },
  { label: 'Email preview', surfaces: ['email_preview'] }
];

// The words a creator gets today, in the send path's order: the draft being written, the approved version, the template's.
const currentWords = state => state?.draft?.content || state?.latestApproved?.content || state?.inCode || null;

const ownsKlaviyoCopy = (surface, { sentFrom, templateName, rulebook }) =>
  isKlaviyoOwnedCopySurface({
    surface,
    sentFrom,
    hasTemplate: !!templateName,
    emailDelivery: rulebook.emailDelivery,
    pushDelivery: rulebook.pushDelivery
  });

const copyIntro = (copy, { sentFrom, templateName, rulebook }) => {
  const present = new Set(copy.map(state => state.surface));
  const owned = surface => ownsKlaviyoCopy(surface, { sentFrom, templateName, rulebook });
  const counted = surface => present.has(surface) && !owned(surface);
  const bodyState = copy.find(state => state.surface === 'email_body');
  const body =
    owned('email_body') || !bodyState
      ? ''
      : bodyState.inCode
      ? ' The email body is in the template file and changes with a pull request.'
      : ' The email body is drafted here until the template lands.';
  const note = _.uniq([...present].map(surface => (owned(surface) ? klaviyoCopyNote(surface) : null)).filter(Boolean))
    .map(line => ` ${line}`)
    .join('');
  if (!sendsFromBackend({ sentFrom, hasTemplate: !!templateName }))
    return `Approve records the words here. They do not change what users receive until they are live in that system.${body}${note}`.trim();
  const sendTime = SEND_TIME_GROUPS.filter(group => group.surfaces.some(counted)).map(group => group.label);
  const signedOff = SIGN_OFF_GROUPS.filter(group => group.surfaces.some(counted)).map(group => group.label);
  const live = sendTime.length
    ? `${_.upperFirst(listWords(sendTime))} ${
        sendTime.length === 1 ? 'changes' : 'change'
      } here: approve a draft and users get it from the next send.`
    : '';
  const record = signedOff.length
    ? `${live ? ' ' : ''}${_.upperFirst(listWords(signedOff))} ${signedOff.length === 1 ? 'is' : 'are'} signed off here and ${
        signedOff.length === 1 ? 'ships' : 'ship'
      } with a pull request.`
    : '';
  return `${live}${record}${body}${note}`.trim();
};

const listWords = words => (words.length <= 1 ? words.join('') : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`);

const SURFACE_TIPS = {
  push_a: TIPS.push,
  push_b: TIPS.push,
  center_row: TIPS.centerRow,
  center_row_several: TIPS.centerRowSeveral,
  center_cta: TIPS.centerCta,
  email_body: TIPS.emailBody
};

// A follower with nothing behind it says so rather than showing an empty field that cannot be typed in.
const borrowedText = state =>
  Object.values(currentWords(state) || {}).join('\n') ||
  (state.status === 'revoked_on_owner'
    ? 'The approval was revoked on the member this row is written on. No member of this shared row has a sentence until it is approved again there.'
    : 'Nothing is approved on the member this row is written on yet.');

const SurfaceBlock = ({
  state,
  inputs,
  entry,
  templateName,
  emailDelivery = null,
  pushDelivery = null,
  path = null,
  step = FIRST_STEP,
  onSaveDraft,
  onWithdrawDraft,
  onApprove,
  onLiveInFlow,
  onWithdraw,
  onRevokeApproval,
  pushBApproved,
  onOpenTemplate,
  onOpenEntry,
  onDraft,
  isDrafting,
  isBusy
}) => {
  const fields = Object.keys(state.limits || {});
  // On the day path the two center sentences are named for when the row uses them, and the chosen
  // step lights the one it uses: the first box while the day has one, otherwise the several row and
  // the case the step names.
  const onPath = !!path?.labels[state.surface];
  const label = onPath ? path.labels[state.surface] : SURFACE_LABELS[state.surface];
  const isLit = onPath && (state.surface === 'center_row' ? step === FIRST_STEP : step !== FIRST_STEP);
  const litCase = onPath && state.surface === 'center_row_several' && step !== FIRST_STEP ? step : null;
  // The field starts as what a creator gets today; the block is keyed on the saved words, so a save remounts it.
  const saved = currentWords(state) || {};
  const [content, setContent] = useState(saved);
  const fieldRefs = useRef({});
  const [isCustomVerb, setIsCustomVerb] = useState(!!saved.verb && !CENTER_CTA_VERBS.includes(saved.verb));
  const hasDraft = state.status === 'in_draft';
  const isOptional = OPTIONAL_SURFACES.includes(state.surface);
  const isFlowSurface = isKlaviyoDeliveredSurface({ surface: state.surface, sentFrom: entry.sentFrom, pushDelivery, emailDelivery });
  const isLiveInFlowSurface = isFlowSurface && state.status === 'live_in_flow';
  // The email body is the template's once one exists; before that it is drafted here like any other surface.
  const isBody = state.surface === 'email_body' && !!state.inCode;
  const isButton = state.surface === 'center_cta';
  const borrowedFrom = state.readOnly ? state.borrowedFrom : null;
  const versionLine = borrowedFrom
    ? `${state.latestApproved ? `v${state.latestApproved.version} · ` : ''}written on ${borrowedFrom}`
    : state.draft
    ? `draft · ${state.draft.agentName || 'person'}${state.draft.updatedAt ? ` · ${moment(state.draft.updatedAt).fromNow()}` : ''}`
    : state.latestApproved
    ? `v${state.latestApproved.version} · approved ${moment(state.latestApproved.approvedAt).format('MMM D')}${
        state.latestApproved.liveInFlowAt ? ' · live in flow' : ''
      }`
    : SURFACE_NOTES[state.surface] || '';
  const statusLabel = state.status === 'needs_copy' && isOptional ? 'Empty' : COPY_STATUS_LABELS[state.status];
  const showStatus = !isButton || hasDraft || !!state.latestApproved;
  // Until someone writes here the fields hold the template's own words, rendered with the Inputs'
  // examples: a brand or a name in them is that example, not copy anyone chose.
  const isRenderedExample = state.status === 'in_code' && !!state.inCode && _.isEqual(content, state.inCode);
  const tag = isBody
    ? `rendered example from the template${templateName ? ` · ${templateName}.js` : ''}, read only`
    : isRenderedExample
    ? 'rendered example · editable'
    : 'editable';
  const pills = copyVariablePills(inputs, state.surface);
  // Checked against the words on screen rather than the last saved ones, so a variable stops being
  // flagged as it is corrected. Same rule the API runs, so the warning cannot disagree with Approve.
  // The template's own words render with those same examples, so they are not checked.
  const { errors, unknownInputs, literalExamples } = validateCopyContent({
    surface: state.surface,
    content,
    inputs: inputs.filter(input => !input.secret),
    secretInputs: inputs.filter(input => input.secret).map(input => input.name),
    caseFields: state.caseFields || [],
    caseEnums: state.caseEnums || {}
  });
  const approvalErrors = state.surface === 'email_body'
    ? validateCopyContent({
        surface: state.surface,
        content,
        inputs: inputs.filter(input => !input.secret),
        secretInputs: inputs.filter(input => input.secret).map(input => input.name),
        requireButtonLink: true
      }).errors
    : errors;
  const exampleLiterals = isRenderedExample ? [] : literalExamples;

  const caseFields = state.caseFields || [];
  const caseEnums = state.caseEnums || {};
  const canHaveCases = supportsCases(state.surface) && caseFields.length > 0;
  // A declared word — a Live Thread's status, or any other enum — is one row each, always all of
  // them, in the order they were declared. Nobody writes the conditions, so the only thing a writer
  // does here is fill in sentences.
  const enumPath = caseFields.find(field => Array.isArray(caseEnums[field])) || null;
  const enumValues = enumPath ? caseEnums[enumPath] : [];
  const isLockedEnum = canHaveCases && !!enumPath;
  const cases = isLockedEnum ? lockedCases(content, enumPath, enumValues) : casesOf(content);
  // An unfinished case is a half-written thought, not a mistake: it holds the save back and says what
  // is missing, rather than being sent to an API that would refuse the whole surface. A declared word
  // with no sentence yet is not unfinished in that way; the launch gate is what asks for the rest. Nor
  // is one left behind by a plan that dropped its word: that is drift, shown on its own row, and holding
  // every other sentence back over it would be the wrong way to report it.
  const checkable = words =>
    isLockedEnum ? casesOf(words).filter(rule => String(rule.text || '').trim() && enumValues.includes(String(rule.value))) : casesOf(words);
  const written = checkable({ cases });
  const caseErrors = written.length ? validateCaseSet({ cases: written, caseFields, caseEnums, fallbackText: content.text ?? '' }).errors : [];
  const conflictedCases = new Set(_.flatten(conflictingCasePairs(cases, caseEnums)));

  // Only a real change becomes a draft: focusing and leaving an approved field must not turn it back into
  // In draft. Emptying every field of a draft withdraws it, which is how an optional Push B is cleared.
  const commit = next => {
    const words = canHaveCases ? withWrittenCases(next || content) : _.omit(next || content, 'cases');
    if (_.isEqual(saved, words)) return;
    if (checkable(words).length && validateCaseSet({ cases: checkable(words), caseFields, caseEnums, fallbackText: words.text ?? '' }).errors.length)
      return;
    if (hasCopyWords(words) || state.draft) onSaveDraft(state.surface, words);
  };

  // A word nobody has written to yet is on screen but not in the draft, so the rest of the system
  // reads a half-filled set as half-filled rather than as sentences that are empty on purpose.
  const withWrittenCases = words => {
    if (!isLockedEnum) return words;
    const filled = casesOf(words).filter(rule => String(rule.text || '').trim());
    return filled.length ? { ...words, cases: filled } : _.omit(words, 'cases');
  };

  const updateCase = (index, patch) => setContent({ ...content, cases: cases.map((rule, at) => (at === index ? { ...rule, ...patch } : rule)) });
  const addCase = () => setContent({ ...content, cases: [...cases, { path: CASE_FIELDS[0], op: CASE_OPERATORS[0], value: '', text: '' }] });
  const removeCase = index => {
    const remaining = cases.filter((rule, at) => at !== index);
    const next = remaining.length ? { ...content, cases: remaining } : _.omit(content, 'cases');
    setContent(next);
    commit(next);
  };

  // Back to the words before anyone typed: the approved version or the template's. A saved draft is
  // withdrawn; typing not yet saved is simply dropped.
  const previous = state.latestApproved?.content || state.inCode || {};
  const canCancel = hasDraft || !_.isEqual(content, saved);
  const cancelEdits = () => {
    const restore = () => {
      setContent(previous);
      setIsCustomVerb(!!previous.verb && !CENTER_CTA_VERBS.includes(previous.verb));
    };
    if (!state.draft) {
      restore();
      return;
    }
    Promise.resolve(onWithdrawDraft(state.surface)).then(result => {
      if (result) restore();
    });
  };

  const fieldLabel = field => {
    if (cases.length && field === 'text') return `In all other cases · ${tag}`;
    return fields.length > 1 ? `${copyFieldLabel(state.surface, field)} · ${tag}` : tag;
  };

  // A case's sentence is addressed as "case:0"; every other field by its own name.
  const caseIndexOf = field => (String(field).startsWith('case:') ? Number(String(field).slice(5)) : null);
  const wordsAt = field => {
    const index = caseIndexOf(field);
    return (index == null ? content[field] : cases[index]?.text) || '';
  };
  const withWordsAt = (field, words) => {
    const index = caseIndexOf(field);
    if (index == null) return { ...content, [field]: words };
    return { ...content, cases: cases.map((rule, at) => (at === index ? { ...rule, text: words } : rule)) };
  };

  // A chip writes into whichever field the writer is in; the rail's own field catches the click when
  // none is. Chips keep focus on mousedown, so the caret is still where it was left.
  const insertVariable = (railField, name) => {
    const target = Object.keys(fieldRefs.current).find(field => fieldRefs.current[field] === document.activeElement) || railField;
    const element = fieldRefs.current[target];
    const value = wordsAt(target);
    const start = element ? element.selectionStart : value.length;
    const end = element ? element.selectionEnd : value.length;
    const variable = `{{ ${name} }}`;
    setContent(withWordsAt(target, value.slice(0, start) + variable + value.slice(end)));
    window.requestAnimationFrame(() => {
      if (!fieldRefs.current[target]) return;
      fieldRefs.current[target].focus();
      fieldRefs.current[target].setSelectionRange(start + variable.length, start + variable.length);
    });
  };

  // A follower shows the words the group renders with and nothing to act on: the draft, approval and
  // revoke all belong to the member they are written on.
  if (borrowedFrom) {
    return (
      <div className={cn('surface borrowed', { lit: isLit })}>
        <div className='surface-head'>
          <span className='surface-name'>
            {label}
            {SURFACE_TIPS[state.surface] && <InfoTip message={SURFACE_TIPS[state.surface]} />}
          </span>
          <span className='surface-version'>{versionLine}</span>
          <span className='surface-actions'>
            <span className={cn('badge', `copy-${state.status}`)}>{statusLabel}</span>
            {onOpenEntry && (
              <button type='button' className='quiet' onClick={() => onOpenEntry(borrowedFrom)}>
                Open {borrowedFrom} ↗
              </button>
            )}
          </span>
        </div>
        <label className='surface-field single read-only'>
          <span className='field-name'>read-only · borrowed from {borrowedFrom}</span>
          <textarea ref={element => matchScrollHeight(element)} rows={1} value={borrowedText(state)} readOnly />
        </label>
      </div>
    );
  }

  if (
    isKlaviyoOwnedCopySurface({
      surface: state.surface,
      sentFrom: entry.sentFrom,
      hasTemplate: !!templateName,
      emailDelivery,
      pushDelivery
    })
  ) {
    const isFlowBody = state.surface === 'email_body' && backendDeliveredByKlaviyo({ sentFrom: entry.sentFrom, pushDelivery });
    const words = isFlowBody
      ? DELIVERED_BY_KLAVIYO_EMAIL_BODY
      : Object.values(currentWords(state) || {})
          .filter(value => typeof value === 'string' && value.trim())
          .join('\n\n');
    if (!words) return null;
    return (
      <div className='surface'>
        <div className='surface-head'>
          <span className='surface-name'>
            {label}
            {SURFACE_TIPS[state.surface] && !isFlowBody && <InfoTip message={SURFACE_TIPS[state.surface]} />}
          </span>
          {!isFlowBody && <span className='surface-version'>{versionLine}</span>}
        </div>
        <label className='surface-field single read-only'>
          <span className='field-name'>{isFlowBody ? 'in the flow' : 'read-only'}</span>
          <textarea ref={element => matchScrollHeight(element)} rows={1} value={words} readOnly />
        </label>
      </div>
    );
  }

  return (
    <div className={cn('surface', { lit: isLit })}>
      <div className='surface-head'>
        <span className='surface-name'>
          {label}
          {SURFACE_TIPS[state.surface] && <InfoTip message={SURFACE_TIPS[state.surface]} />}
        </span>
        <span className='surface-version'>{versionLine}</span>
        <span className='surface-actions'>
          {showStatus && <span className={cn('badge', `copy-${state.status}`)}>{statusLabel}</span>}
          {DRAFTABLE_SURFACES.includes(state.surface) && !isBody && (
            <DraftButton onClick={onDraft} isDrafting={isDrafting} isBusy={isBusy} title={TIPS.draftWithAi} />
          )}
          {canCancel && !isBody && (
            <button type='button' className='quiet' onMouseDown={e => e.preventDefault()} onClick={cancelEdits}>
              Cancel edits
            </button>
          )}
          {hasDraft && (
            <button
              type='button'
              className='approve'
              onClick={() => onApprove(state.surface)}
              disabled={
                approvalErrors.length > 0 || shouldRefuseUnknownCopyInputs({ unknownInputs, hasTemplate: !!templateName }) || caseErrors.length > 0 || exampleLiterals.length > 0
              }
            >
              Approve
            </button>
          )}
          {state.latestApproved && (!isOptional || hasDraft) && !(state.surface === 'push_a' && pushBApproved) && !isLiveInFlowSurface && (
            <button type='button' className='quiet' onClick={() => onRevokeApproval(state.surface)}>
              Revoke approval
            </button>
          )}
          {isOptional && state.latestApproved && !hasDraft && !isLiveInFlowSurface && (
            <button type='button' className='quiet' onClick={() => onWithdraw(state.surface)}>
              Withdraw
            </button>
          )}
          {(entry.sentFrom === 'klaviyo_flow' || isFlowSurface) && ['approved', 'live_in_flow'].includes(state.status) && (
            <button type='button' className='quiet' onClick={() => onLiveInFlow(state.surface, state.status === 'approved')}>
              {state.status === 'approved' ? 'Live in flow' : 'Move back to Approved'}
            </button>
          )}
          {isBody && onOpenTemplate && state.inCode && (
            <button type='button' className='quiet' onClick={onOpenTemplate}>
              Open in Templates
            </button>
          )}
        </span>
      </div>

      {onPath && <p className='surface-why'>{path.why[state.surface]}</p>}

      {isBody && (
        <label className='surface-field single read-only'>
          <span className='field-name'>{tag}</span>
          <textarea
            ref={element => matchScrollHeight(element)}
            rows={1}
            value={`${state.inCode.text}\n\n… the full email, with header and footer, is in Templates.`}
            readOnly
          />
        </label>
      )}

      {isButton && (
        <label className='surface-field single'>
          <span className='field-name'>editable</span>
          <ButtonVerbField
            verb={content.verb || ''}
            isCustom={isCustomVerb}
            onPick={verb => {
              setIsCustomVerb(false);
              const next = { verb };
              setContent(next);
              commit(next);
            }}
            onCustom={() => setIsCustomVerb(true)}
            onType={verb => setContent({ ...content, verb })}
            onBlur={() => commit()}
          />
        </label>
      )}

      {canHaveCases &&
        cases.map((rule, index) => (
          <CaseBlock
            key={index}
            index={index}
            rule={rule}
            caseFields={caseFields}
            lockedLabel={
              isLockedEnum
                ? `${enumPath === THREAD_CASE_FIELD ? statusLabelOf(rule.value) : rule.value}${
                    enumValues.includes(String(rule.value)) ? '' : ' · no longer sent'
                  }`
                : null
            }
            lockedLead={enumPath === THREAD_CASE_FIELD ? 'when it becomes' : 'when it is'}
            conditionWords={isLockedEnum && enumValues.includes(String(rule.value)) ? path?.conditions[rule.value] : null}
            lit={litCase != null && caseStepKey(rule.value) === litCase}
            limit={state.limits.text}
            inputs={inputs}
            pills={pills}
            conflicted={conflictedCases.has(index)}
            registerRef={element => (fieldRefs.current[`case:${index}`] = element)}
            onChange={patch => updateCase(index, patch)}
            onCommit={() => commit()}
            onRemove={() => removeCase(index)}
            onInsert={name => insertVariable(`case:${index}`, name)}
          />
        ))}

      {!isBody &&
        !isButton &&
        fields
          .filter(field => !isLockedEnum || field !== 'text')
          .map(field => {
            const limit = state.limits[field];
            const value = content[field] || '';
            const renderedLength = interpolateWithExamples(value, inputs).length;
            const overLimit = limit && renderedLength > limit;
            const onChange = e => setContent({ ...content, [field]: e.target.value });
            return (
              <label key={field} className={cn('surface-field', { over: overLimit, single: fields.length === 1 })}>
                <span className='field-name'>{fieldLabel(field)}</span>
                {field === 'text' || field === 'body' ? (
                  <textarea
                    ref={element => {
                      fieldRefs.current[field] = element;
                      matchScrollHeight(element);
                    }}
                    rows={1}
                    value={value}
                    onChange={onChange}
                    onBlur={() => commit()}
                  />
                ) : (
                  <input ref={element => (fieldRefs.current[field] = element)} value={value} onChange={onChange} onBlur={() => commit()} />
                )}
                {limit && (
                  <span className={cn('counter', { over: overLimit })}>
                    {renderedLength} / {limit} recommended
                  </span>
                )}
                <span className='field-guide'>{fieldGuideFor(state.surface, field)}</span>
                {field !== 'button' && <VariableRail pills={pills} hasInputs={inputs.length > 0} onInsert={name => insertVariable(field, name)} />}
              </label>
            );
          })}

      {canHaveCases && (
        <div className='case-controls'>
          {!isLockedEnum && (
            <button type='button' className='quiet' onClick={addCase} disabled={cases.length >= MAX_CASES}>
              Add a case
            </button>
          )}
          <span className='case-note'>
            {isLockedEnum
              ? enumPath === THREAD_CASE_FIELD
                ? 'The row is rewritten with the sentence for whichever status arrives, so every status needs one.'
                : onPath
                ? path.caseNote
                : 'The row uses the sentence for whichever value arrives, so every value needs one.'
              : cases.length
              ? 'Each case is checked on its own, so no two of them may be true at once.'
              : 'One sentence covers every day. Add a case when a particular day needs different words.'}
          </span>
        </div>
      )}

      {state.surface === 'email_body' && errors.length > 0 && <div className='surface-warning'>{errors.join(' ')}</div>}
      {approvalErrors.length > errors.length && (
        <div className='surface-warning'>
          {approvalErrors.filter(error => !errors.includes(error)).join(' ')} The draft still saves; resolve this before approval.
        </div>
      )}
      {caseErrors.length > 0 && <div className='surface-warning'>{caseErrors.join(' ')} The draft saves once this is resolved.</div>}

      {exampleLiterals.length > 0 && (
        <div className='surface-warning'>
          {exampleLiterals.map(input => {
            const variable = literalExampleVariable(input);
            return (
              <span key={input.name}>
                <code>{String(input.example)}</code> is the example for{' '}
                {variable ? (
                  <>
                    <code>{`{{ ${variable} }}`}</code>; use the variable so every user gets their own.
                  </>
                ) : (
                  <>
                    <code>{input.name}</code>, which no variable carries; leave it out so it does not go to every user.
                  </>
                )}{' '}
              </span>
            );
          })}
        </div>
      )}
      {unknownInputs.length > 0 && (
        <div className='surface-warning'>
          {unknownInputs.map(name => (
            <code key={name}>{name}</code>
          ))}{' '}
          {unknownInputs.length === 1 ? 'is' : 'are'} not in Inputs.{' '}
          {templateName ? (
            <>Available: {inputs.length ? inputs.map(input => <code key={input.name}>{input.name}</code>) : 'none yet'}.</>
          ) : (
            'Add as an input in the side panel'
          )}
        </div>
      )}
    </div>
  );
};

SurfaceBlock.propTypes = {
  state: PropTypes.object.isRequired,
  inputs: PropTypes.array.isRequired,
  entry: PropTypes.object.isRequired,
  templateName: PropTypes.string,
  emailDelivery: PropTypes.string,
  pushDelivery: PropTypes.string,
  path: PropTypes.object,
  step: PropTypes.string,
  onSaveDraft: PropTypes.func.isRequired,
  onWithdrawDraft: PropTypes.func.isRequired,
  onApprove: PropTypes.func.isRequired,
  onLiveInFlow: PropTypes.func.isRequired,
  onWithdraw: PropTypes.func.isRequired,
  onRevokeApproval: PropTypes.func.isRequired,
  pushBApproved: PropTypes.bool,
  onOpenTemplate: PropTypes.func,
  onOpenEntry: PropTypes.func,
  onDraft: PropTypes.func,
  isDrafting: PropTypes.bool,
  isBusy: PropTypes.bool
};

// One case: the condition that selects it and the sentence it shows. Order is presentation only, so
// a case that can be true at the same time as another is marked here rather than quietly ranked.
// A locked case states its condition instead of offering it: the thread's statuses are the plan's.
const CaseBlock = ({
  index,
  rule,
  caseFields,
  lockedLabel = null,
  lockedLead = 'when it becomes',
  conditionWords = null,
  lit = false,
  limit,
  inputs,
  pills,
  conflicted,
  registerRef,
  onChange,
  onCommit,
  onRemove,
  onInsert
}) => {
  const value = rule.text || '';
  const renderedLength = interpolateWithExamples(value, inputs).length;
  const overLimit = limit && renderedLength > limit;
  return (
    <div className={cn('case-block', { conflicted, locked: !!lockedLabel, lit })}>
      <div className='case-condition'>
        {lockedLabel && conditionWords ? (
          <>
            <span className='case-number'>{index + 1}</span>
            <span className='case-when'>when</span>
            <span className='case-locked'>{conditionWords}</span>
            <code className='case-enum'>{lockedLabel}</code>
          </>
        ) : lockedLabel ? (
          <>
            <span className='case-number'>{index + 1}</span>
            <span className='case-when'>{lockedLead}</span>
            <span className='case-locked'>{lockedLabel}</span>
          </>
        ) : (
          <>
            <span className='case-number'>Case {index + 1}</span>
            <span className='case-when'>when</span>
            <select value={rule.path || ''} onChange={e => onChange({ path: e.target.value })} onBlur={onCommit}>
              {caseFields.map(field => (
                <option key={field} value={field}>
                  {caseFieldLabel(field)}
                </option>
              ))}
            </select>
            <select value={rule.op || ''} onChange={e => onChange({ op: e.target.value })} onBlur={onCommit}>
              {CASE_OPERATORS.map(op => (
                <option key={op} value={op}>
                  {CASE_OPERATOR_LABELS[op]}
                </option>
              ))}
            </select>
            <input
              className='case-value'
              type='number'
              min='0'
              value={rule.value ?? ''}
              onChange={e => onChange({ value: e.target.value === '' ? '' : Number(e.target.value) })}
              onBlur={onCommit}
            />
            <button type='button' className='quiet' onClick={onRemove}>
              Remove
            </button>
          </>
        )}
      </div>
      <label className={cn('surface-field single', { over: overLimit })}>
        <textarea
          ref={element => {
            registerRef(element);
            matchScrollHeight(element);
          }}
          rows={1}
          value={value}
          onChange={e => onChange({ text: e.target.value })}
          onBlur={onCommit}
        />
        {limit && (
          <span className={cn('counter', { over: overLimit })}>
            {renderedLength} / {limit} recommended
          </span>
        )}
        <VariableRail pills={pills} hasInputs={inputs.length > 0} onInsert={onInsert} />
      </label>
      {conflicted && <div className='case-conflict'>This can be true at the same time as another case. Narrow one of them.</div>}
    </div>
  );
};

CaseBlock.propTypes = {
  index: PropTypes.number.isRequired,
  rule: PropTypes.object.isRequired,
  caseFields: PropTypes.array.isRequired,
  lockedLabel: PropTypes.string,
  lockedLead: PropTypes.string,
  conditionWords: PropTypes.string,
  lit: PropTypes.bool,
  limit: PropTypes.number,
  inputs: PropTypes.array.isRequired,
  pills: PropTypes.array.isRequired,
  conflicted: PropTypes.bool,
  registerRef: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onCommit: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onInsert: PropTypes.func.isRequired
};

// Past this many the rail collapses rather than wrapping to a third line. Two of the 82 templates reach it.
const RAIL_LIMIT = 6;

// Every variable this notification can render, under the field it writes into. The rail is the whole
// vocabulary, so a writer learns what is available without approving and being refused. A pill's
// hover says what the variable means; the value on it is only an example.
const VariableRail = ({ pills, hasInputs, onInsert }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const overflows = pills.length > RAIL_LIMIT;
  const shown = isExpanded || !overflows ? pills : pills.slice(0, RAIL_LIMIT);
  return (
    <div className='variable-rail'>
      <span className='rail-label'>Insert</span>
      {shown.map(pill => {
        const chip = (
          <button type='button' className='chip' onMouseDown={e => e.preventDefault()} onClick={() => onInsert(pill.name)}>
            <code>{pill.name}</code>
            {pill.example != null && pill.example !== '' && <span className='example'>{String(pill.example)}</span>}
          </button>
        );
        return pill.meaning ? (
          <Tooltip key={pill.name} outerClassName='chip-tip' message={pill.meaning} position='top'>
            {chip}
          </Tooltip>
        ) : (
          <React.Fragment key={pill.name}>{chip}</React.Fragment>
        );
      })}
      {overflows && (
        <button type='button' className='chip more' onMouseDown={e => e.preventDefault()} onClick={() => setIsExpanded(!isExpanded)}>
          {isExpanded ? 'Show fewer' : `+${pills.length - RAIL_LIMIT} more`}
        </button>
      )}
      {!hasInputs && <span className='rail-empty'>No Inputs on this notification yet — add them on the Plan, or Draft with AI.</span>}
    </div>
  );
};

VariableRail.propTypes = {
  pills: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, example: PropTypes.any, meaning: PropTypes.string })).isRequired,
  hasInputs: PropTypes.bool.isRequired,
  onInsert: PropTypes.func.isRequired
};

// The push, the Notification Center row and the email as creators would see them, with the inputs'
// example values. The email only appears for a backend send: the template's body once one exists,
// the body drafted here before that, which the page brings already set in the starter's layout.
// A Klaviyo-built email lives in the flow, not here.
const PreviewCard = ({ copy, inputs, templateName, emailPreview }) => {
  const [email, setEmail] = useState(null);
  const words = surface => currentWords(copy.find(candidate => candidate.surface === surface));
  const push = words('push_a');
  const row = words('center_row');
  const rowText = row?.text || casesOf(row)[0]?.text;
  const hasEmailBodyInCode = !!copy.find(candidate => candidate.surface === 'email_body')?.inCode;
  const draftedHtml = emailPreview?.html || null;

  useEffect(() => {
    setEmail(null);
    if (!templateName || !hasEmailBodyInCode) return;
    let cancelled = false;
    renderEmailTemplate(templateName)
      .then(resp => !cancelled && setEmail(resp?.html ? resp : null))
      .catch(err => {
        if (cancelled) return;
        setEmail(null);
        window.ALERT?.error?.(err?.error || err?.message || 'Could not render the email template for the preview.');
      });
    return () => (cancelled = true);
  }, [templateName, hasEmailBodyInCode]);

  if (!push && !rowText && !email && !draftedHtml) return null;
  const subject = words('email_subject')?.text;
  const previewText = words('email_preview')?.text;
  return (
    <div className='card'>
      <div className='card-head'>
        <span className='card-title'>Preview</span>
        <span className='card-note'>example values from Inputs</span>
      </div>
      <div className='preview-tiles'>
        {push && (
          <div className='preview-tile dark'>
            <div className='tile-label'>Push A · iOS</div>
            <PushPreview push={{ title: interpolateWithExamples(push.title, inputs), subtitle: interpolateWithExamples(push.body, inputs) }} />
          </div>
        )}
        {rowText && (
          <div className='preview-tile'>
            <div className='tile-label'>Notification Center row</div>
            <div className='center-row-preview'>
              <span className='lead'>S</span>
              <span className='sentence'>{interpolateWithExamples(rowText, inputs)}</span>
              <span className='time'>9m</span>
            </div>
          </div>
        )}
        {(email || draftedHtml) && (
          <div className='preview-tile wide'>
            <div className='tile-label'>Email</div>
            <EmailPreview
              subject={subject ? interpolateWithExamples(subject, inputs) : email?.subject}
              previewText={previewText ? interpolateWithExamples(previewText, inputs) : null}
              html={email?.html || draftedHtml}
              source={email ? 'template' : 'drafted'}
            />
          </div>
        )}
      </div>
    </div>
  );
};

PreviewCard.propTypes = {
  copy: PropTypes.array.isRequired,
  inputs: PropTypes.array.isRequired,
  templateName: PropTypes.string,
  emailPreview: PropTypes.shape({ html: PropTypes.string })
};

const HistoryCard = ({ history }) => (
  <div className='card'>
    <div className='card-head'>
      <span className='card-title'>History</span>
    </div>
    <div className='history'>
      {history.map((line, index) => (
        <div key={index} className='history-line'>
          <span className='when'>{moment(line.at).format('MMM D · HH:mm')}</span>
          <span className='who'>{line.who}</span>
          <span className='what'>{line.what}</span>
        </div>
      ))}
      {!history.length && <div className='history-line muted'>Nothing yet.</div>}
    </div>
  </div>
);

HistoryCard.propTypes = { history: PropTypes.array.isRequired };

export default NotificationsRegistryEntry;
